<script setup>
// Every section on the screen is one of these: a card with a header bar (the
// club-colour icon block, the title, and any detail on the right) and a body.
// One size everywhere, so the feature panels and the sidebar line up.
defineProps({
  // No icon and title: a plain card with no header bar
  icon: {type: String, default: null},
  title: {type: String, default: null},
  subtitle: {type: String, default: null},
  sport: {type: String, default: null},
  page: {type: Number, default: 0},
  pages: {type: Number, default: 1},
  // Stretch to the height it is given (a section alone in its column)
  fill: {type: Boolean, default: false},
});
</script>

<template>
  <section class="tv-sec" :class="{'tv-sec--fill': fill}">
    <header v-if="title" class="tv-sec-bar">
      <span class="tv-sec-icon"><v-icon :icon="icon" size="30"/></span>
      <h2 class="tv-sec-title">{{ title }}</h2>
      <span v-if="subtitle" class="tv-sec-sub">{{ subtitle }}</span>
      <span class="tv-sec-end">
        <span v-if="sport" class="tv-sec-sport">{{ sport }}</span>
        <span v-if="pages > 1" class="tv-sec-pages">
          <span v-for="n in pages" :key="n" class="tv-sec-pip" :class="{'tv-sec-pip--on': n - 1 === page}"/>
        </span>
      </span>
    </header>
    <div class="tv-sec-body">
      <slot/>
    </div>
    <footer v-if="$slots.footer" class="tv-sec-foot">
      <slot name="footer"/>
    </footer>
  </section>
</template>

<style scoped lang="scss">
.tv-sec {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  border-radius: 10px;
  background: var(--tv-panel);
  border: 1px solid var(--tv-line);
  overflow: hidden;
}

.tv-sec--fill {
  flex: 1 1 auto;
}

.tv-sec-bar {
  display: flex;
  align-items: center;
  gap: 18px;
  height: 64px;
  flex-shrink: 0;
  padding-right: 20px;
  border-bottom: 1px solid var(--tv-line);
  background: var(--tv-bar);
}

.tv-sec-icon {
  align-self: stretch;
  width: 76px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-right: 10px;
  background: var(--tv-accent);
  color: var(--tv-on-accent);
  clip-path: polygon(0 0, 100% 0, calc(100% - 16px) 100%, 0 100%);
  flex-shrink: 0;
}

.tv-sec-title {
  margin: 0;
  font-family: var(--tv-display);
  font-weight: 800;
  font-size: 38px;
  /* Room for descenders (g, y, p), Poppins' especially: the title truncates
     with overflow hidden */
  line-height: 1.5;
  color: var(--tv-ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
  min-width: 0;
}

.tv-sec-sub {
  /* The division: kept whole, the title gives way first */
  flex-shrink: 0;
  font-size: 20px;
  color: var(--tv-muted);
  line-height: 1.4;
  white-space: nowrap;
}

.tv-sec-end {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
}

.tv-sec-sport {
  padding: 2px 12px;
  border-radius: 5px;
  border: 1px solid var(--tv-line);
  font-size: 18px;
  color: var(--tv-muted);
}

.tv-sec-pages {
  display: flex;
  gap: 8px;
}

.tv-sec-pip {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--tv-line);
}

.tv-sec-pip--on {
  background: var(--tv-accent);
}

.tv-sec-body {
  flex: 1;
  min-height: 0;
  padding: 16px 20px 18px;
}

/* A headless card's content sits evenly inside it */
.tv-sec-bar + .tv-sec-body {
  padding-top: 16px;
}

.tv-sec > .tv-sec-body:first-child {
  padding: 20px;
}

.tv-sec-foot {
  flex-shrink: 0;
  padding: 14px 20px;
  border-top: 1px solid var(--tv-line);
  background: var(--tv-bar);
}
</style>
